import {
  GridColumnVisibilityModel,
  GridDensity,
  GridStateColDef,
} from '@bymason-private/legacy-pattern-components';

export type GridGroupingOption = 'grouped' | 'flat';

// browser storage keys for views
// DEPRECATED - use getBrowserStorageKey
// export const ARTIFACTS_LIST_OPTION_KEY = 'ux-artifacts-list-option';
// export const GROUP_DEPLOYMENTS_LIST_OPTION_KEY = 'ux-group-deployments-list-option';

type BrowserStorageKeyType =
  | 'build-with-cli'
  | 'grid-column-state'
  | 'grid-column-density'
  | 'grid-column-visibility-state'
  | 'grid-grouping-option'
  | 'device-search-operator'
  | 'provision-session-info'
  | 'controller-mfa-acked';

/**
 * Retreives a page or global scoped key for browser storage
 * @param {BrowserStorageKeyType} keyType - Defined key string
 * @param {boolean} global - Overrides the default path scope suffix with 'global' suffix
 * @param {string} overrideSuffix - Overrides the defalut scope with a custom suffix
 * @returns {string} - The fully scoped key
 */
export const getBrowserStorageKey = (keyType: BrowserStorageKeyType, global = false, overrideSuffix = '') => {
  const pageId = global ? 'global' : window.location.pathname.replace('controller2', 'controller');
  return `ux-${keyType}${overrideSuffix ? `-${overrideSuffix}` : `-${pageId}`}`;
};

/**
 * get and set grid column state in local storage
 * this key is scoped per page
 */
interface GridColState {
  field: string;
  flex?: number | null;
  hide?: boolean;
  width?: number;
  position: number;
}
type PersistedGridColState = Record<string, GridColState>;

const gridColStateKey = () => getBrowserStorageKey('grid-column-state');

export const persistGridColumnState = (newColDef?: GridStateColDef[]) => {
  const colStateMap = newColDef?.reduce<PersistedGridColState>((acc, { field, hide, width, flex }, idx) => {
    return {
      ...acc,
      [field]: {
        field,
        hide,
        flex: flex || null,
        width,
        position: idx,
      },
    };
  }, {});

  localStorage?.setItem(gridColStateKey(), JSON.stringify(colStateMap));
};

export const retreiveGridColumnState = (): PersistedGridColState => {
  try {
    return JSON.parse(localStorage?.getItem(gridColStateKey()) || '') || {};
  } catch {
    return {};
  }
};

/**
 * get and set grid density setting in local storage
 * this key is scoped to per page
 */
const gridDensityKey = () => getBrowserStorageKey('grid-column-density');

export const persistGridDensity = (density: GridDensity) => {
  localStorage?.setItem(gridDensityKey(), density);
};

export const retreiveGridDensity = () => {
  try {
    return localStorage?.getItem(gridDensityKey()) as GridDensity;
  } catch {
    return undefined;
  }
};

/**
 * get and set grid column visibility setting in local storage
 * this key is scoped to per page
 */
const gridColVisibilityKey = () => getBrowserStorageKey('grid-column-visibility-state');

export const persistGridColumnVisibility = (visibility: GridColumnVisibilityModel) => {
  localStorage?.setItem(gridColVisibilityKey(), JSON.stringify(visibility));
};

export const retreiveGridColumnVisibility = () => {
  try {
    return (JSON.parse(localStorage?.getItem(gridColVisibilityKey()) || '') ||
      {}) as GridColumnVisibilityModel;
  } catch {
    return {};
  }
};

/**
 * get and set grid grouping setting in local storage
 * this key is scoped to 'artifact' or 'deploy' list
 */
type GridGroupingScope = 'artifacts' | 'deploys';

const gridGroupingKey = (suffix: GridGroupingScope) =>
  getBrowserStorageKey('grid-grouping-option', true, suffix);

export const persistGridGrouping = (scope: GridGroupingScope, value: GridGroupingOption) => {
  localStorage?.setItem(gridGroupingKey(scope), value);
};

export const retreiveGridGrouping = (scope: GridGroupingScope) => {
  try {
    const lsv = localStorage?.getItem(gridGroupingKey(scope));
    return lsv ? (lsv as GridGroupingOption) : 'grouped';
  } catch {
    return 'grouped';
  }
};

/**
 * get and set build type setting in local storage
 * this key is globally scoped
 */
const buildWithCliKey = () => getBrowserStorageKey('build-with-cli', true);

export const persistBuildWithCli = (value: boolean) => {
  localStorage?.setItem(buildWithCliKey(), value === true ? 'true' : 'false');
};

export const retreiveBuildWithCli = () => {
  try {
    return localStorage?.getItem(buildWithCliKey()) === 'true';
  } catch {
    return false;
  }
};

/**
 * get and set session flag that will only show the welcome
 * dialog once per session. We need to supress if there is
 * a problem setting the controller version pref upon acknowleding
 */
const controllerMfaAckedKey = () => getBrowserStorageKey('controller-mfa-acked', true);

export const persistControllerMfaAcked = () => {
  const ttl = 1000 * 60 * 60 * 24 * 7; // 7 days
  const now = new Date();
  localStorage?.setItem(controllerMfaAckedKey(), `${now.getTime() + ttl}`);
};

export const retreiveControllerMfaAcked = (): boolean => {
  const now = new Date();
  try {
    const isExpired = parseInt(localStorage?.getItem(controllerMfaAckedKey()) || '0') < now.getTime();
    return !isExpired;
  } catch {
    return false;
  }
};

const provisionSessionInfoKey = (sessionId: string) =>
  getBrowserStorageKey('provision-session-info', true, sessionId);

export const persistProvisionSessionInfo = (sessionId: string, info: unknown) => {
  localStorage?.setItem(provisionSessionInfoKey(sessionId), JSON.stringify(info));
};

export const retreiveProvisionSessionInfo = (sessionId: string) => {
  try {
    return JSON.parse(localStorage?.getItem(provisionSessionInfoKey(sessionId)) || '') as unknown;
  } catch {
    return null;
  }
};

export const removeProvisionSessionInfo = (sessionId: string) => {
  localStorage?.removeItem(provisionSessionInfoKey(sessionId));
};
